import './ui.css';
import { useEffect, useState, type ButtonHTMLAttributes, type FormEventHandler, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TableHTMLAttributes } from 'react';
import { Link } from 'react-router-dom';
import { publicSourcePage } from '../lib/public-source-page.js';

export function PageIntro({ eyebrow, title, description, children, className = '' }: { eyebrow?: string; title: string; description?: string; children?: ReactNode; className?: string }) {
  return <section className={`page-intro ${className}`.trim()}><div>{eyebrow && <p className="eyebrow">{eyebrow}</p>}<h1>{title}</h1>{description && <p className="page-description">{description}</p>}</div>{children}</section>;
}

export function Panel({ children, className = '', ariaLabel }: { children: ReactNode; className?: string; ariaLabel?: string }) {
  return <section className={`panel ${className}`} aria-label={ariaLabel}>{children}</section>;
}

export function FilterPanel({ children, className = '', ariaLabel, as = 'section', onSubmit }: { children: ReactNode; className?: string; ariaLabel?: string; as?: 'section' | 'form'; onSubmit?: FormEventHandler<HTMLFormElement> }) {
  const classes = `panel filter-panel ${className}`.trim();
  if (as === 'form') return <form className={classes} onSubmit={onSubmit} aria-label={ariaLabel}>{children}</form>;
  return <Panel className={`filter-panel ${className}`.trim()} {...(ariaLabel ? { ariaLabel } : {})}>{children}</Panel>;
}

interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  labelClassName?: string;
  children: ReactNode;
}

export function SelectField({ label, value, onValueChange, labelClassName = '', children, ...props }: SelectFieldProps) {
  return <label className={`form-field ${labelClassName}`.trim()}>{label}<select {...props} value={value} onChange={(event) => onValueChange(event.target.value)}>{children}</select></label>;
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  labelClassName?: string;
}

export function TextField({ label, value, onValueChange, labelClassName = '', ...props }: TextFieldProps) {
  return <label className={`form-field ${labelClassName}`.trim()}>{label}<input {...props} value={value} onChange={(event) => onValueChange(event.target.value)} /></label>;
}

export function Button({ variant = 'secondary', className = '', type = 'button', children, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'unstyled' }) {
  return <button {...props} type={type} className={`${variant === 'unstyled' ? '' : variant} ${className}`.trim()}>{children}</button>;
}

export function MetricCard({ label, value, detail, className = '', panel = true }: { label: ReactNode; value: ReactNode; detail?: ReactNode; className?: string; panel?: boolean }) {
  return <article className={`${panel ? 'panel ' : ''}metric-card ${className}`.trim()}><span>{label}</span><strong>{value}</strong>{detail != null && <small>{detail}</small>}</article>;
}

type DataTableProps = Omit<TableHTMLAttributes<HTMLTableElement>, 'className'> & { children: ReactNode; className?: string; tableClassName?: string };

export function DataTable({ children, className = '', tableClassName = '', ...props }: DataTableProps) {
  return <div className={`table-scroll ${className}`.trim()}><table {...props} className={tableClassName}>{children}</table></div>;
}

export function ColorSwatch({ color, className = '' }: { color: string; className?: string }) {
  return <svg className={`color-swatch ${className}`.trim()} viewBox="0 0 12 12" aria-hidden="true"><circle cx="6" cy="6" r="5" fill={color} /></svg>;
}

export function ProgressBar({ value, label, className = '', max = 100 }: { value: number; label: string; className?: string; max?: number }) {
  const normalizedValue = Number.isFinite(value) ? Math.min(max, Math.max(0, value)) : 0;
  return <progress className={`progress-bar ${className}`.trim()} value={normalizedValue} max={max} aria-label={label}>{normalizedValue}%</progress>;
}

export function Loading({ label = 'Carregando informações…' }: { label?: string }) {
  return <div className="loading" role="status"><span className="loading-indicator" />{label}</div>;
}

export function RequestError({ message }: { message: string }) {
  return <div className="notice error" role="alert"><strong>Não foi possível carregar estas informações.</strong><p>{message}</p><p>Confira se a API e o banco estão disponíveis e tente novamente.</p></div>;
}

export function EmptyState({ title, description, to = '/politicos', action = 'Voltar à consulta' }: { title: string; description?: string; to?: string; action?: string }) {
  return <Panel className="empty-state"><h2>{title}</h2>{description && <p>{description}</p>}<Link className="secondary" to={to}>{action}</Link></Panel>;
}

export function DataNote({ children }: { children: ReactNode }) {
  return <p className="small data-note"><span aria-hidden="true">ⓘ</span> {children}</p>;
}

export function Pagination({ page, size, total, onChange, showJump = true, pageLabel, ariaLabel = 'Paginação', className = '' }: { page: number; size: number; total: number; onChange: (page: number) => void; showJump?: boolean; pageLabel?: ReactNode; ariaLabel?: string; className?: string }) {
  const pages = Math.ceil(total / size);
  const [jumpValue, setJumpValue] = useState(String(page));
  useEffect(() => setJumpValue(String(page)), [page]);
  if (pages <= 1) return null;
  const go = (n: number) => {
    if (!Number.isFinite(n)) return;
    onChange(Math.max(1, Math.min(pages, Math.trunc(n))));
  };
  const goToEnteredPage = () => {
    if (!jumpValue.trim()) return;
    go(Number(jumpValue));
  };
  return <nav className={`pagination ${className}`.trim()} aria-label={ariaLabel}>
    <Button disabled={page <= 1} onClick={() => go(1)}>Primeira</Button>
    <Button disabled={page <= 1} onClick={() => go(page - 1)}>Anterior</Button>
    <span className="small" aria-live="polite">{pageLabel ?? `${page} de ${pages}`}</span>
    <Button disabled={page >= pages} onClick={() => go(page + 1)}>Próxima</Button>
    <Button disabled={page >= pages} onClick={() => go(pages)}>Última</Button>
    {showJump && <><label className="pagination-jump">Ir para página <input type="number" min="1" max={pages} step="1" value={jumpValue} onChange={(event) => setJumpValue(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') goToEnteredPage(); }} /></label>
      <Button onClick={goToEnteredPage}>Ir</Button></>}
  </nav>;
}

export function SourceLink({ href, children = 'Fonte oficial' }: { href?: string | null; children?: ReactNode }) {
  const page = publicSourcePage(href);
  if (!page) return <span className="small muted">Fonte não informada no registro</span>;
  return <a className="source-link" href={page} target="_blank" rel="noreferrer">{children} ↗</a>;
}

